import { appendWithinBudget, compactText } from "./compaction";
/**
 * Per-node run transcript ("chat section per node", flow-run-inspector
 * roadmap 2026-07-02): the same stream the chat window shows for a running
 * node — prompt, thinking, streamed content, tool lines, result, decision —
 * accumulated per node and rendered in the RunInspector side panel.
 *
 * The extension host keeps its own mirror of {@link appendTrace}
 * (thinkinghub-vscode/src/flows/runTrace.ts — the host tsc pass doesn't
 * import this webview package); both sides apply the same deterministic
 * coalescing to the same (kind, text) chunk stream, so live state and
 * persisted/replayed state converge.
 */

export type NodeTraceKind =
  | "prompt"
  | "thinking"
  | "content"
  | "tool"
  | "result"
  | "error"
  | "transition";

export interface NodeTraceEntry {
  kind: NodeTraceKind;
  text: string;
  at: number;
}

/** Per-entry text budget — a runaway node can't flood the panel/state. */
export const MAX_TRACE_ENTRY_CHARS = 4000;
/** Per-node entry budget — oldest non-prompt entries drop first. */
export const MAX_TRACE_ENTRIES = 200;

const TRUNCATION_MARK = "\n…[truncated]";

/**
 * Append one streamed chunk to a node's transcript, in place.
 * Consecutive `thinking`/`content` chunks coalesce into the tail entry
 * (thinker streams arrive as many small pieces); every other kind starts a
 * fresh entry. Entries clamp at {@link MAX_TRACE_ENTRY_CHARS}; the list
 * clamps at {@link MAX_TRACE_ENTRIES}, preserving the opening `prompt` entry.
 */
export function appendTrace(
  list: NodeTraceEntry[],
  kind: NodeTraceKind,
  text: string,
  at: number
): void {
  if (!text) {
    return;
  }
  const last = list[list.length - 1];
  const coalesces =
    (kind === "thinking" || kind === "content") &&
    last?.kind === kind &&
    !last.text.endsWith(TRUNCATION_MARK);
  if (coalesces) {
    // Keep BOTH ends as the answer streams: the old rule dropped every chunk once the entry filled,
    // which meant a long answer's conclusion — the part a flow routes on — never appeared at all.
    last.text = appendWithinBudget(last.text, text, MAX_TRACE_ENTRY_CHARS);
    return;
  }
  // Head-and-tail rather than a head slice: a node's answer ends with its conclusion, and a
  // transcript that drops the ending is a transcript of everything except the point.
  list.push({
    kind,
    text: compactText(text, { budget: MAX_TRACE_ENTRY_CHARS, strategy: "head-tail" }).text,
    at,
  });
  while (list.length > MAX_TRACE_ENTRIES) {
    // Keep the prompt (entry 0 in the normal flow) so the transcript never
    // loses what the node was asked to do.
    const dropIdx = list[0]?.kind === "prompt" ? 1 : 0;
    list.splice(dropIdx, 1);
  }
}
